/**
 * 「大号已用时间」面板 —— **全项目唯一的一份计时 UI**。
 *
 * ## 它为什么必须只有一个
 *
 * 2026-10-17 用户实测报的 bug：
 *
 * > 孩子端这里点了暂停后，过一段时间想重新开始，就变成这样，**看不到那个计时的了**。
 *
 * 截图里那一项是「计时中」+「暂停 / 完成」按钮，**唯独没有那个大的已用时间**。
 * 根因不是计时坏了，而是这块 JSX 当时**被抄了两份**：
 *
 * | 视图 | 当时 | 结果 |
 * | --- | --- | --- |
 * | 「今天的作业」（`StudentHomeworkPage` 的 `HomeworkItemRow`） | 手写了一份 | 有数字 ✅ |
 * | 今日打卡页（`StudentTodayPage` 的 `TaskRow`） | 又手写了一份（逐字相同） | 有数字 ✅ |
 * | 「超时」/「**还有效**」行（`components/homework/OverdueRow.tsx`） | **没有抄** | 只有一句"正在计时中"，**没有数字** ❌ |
 *
 * 所以这里把"数字 + 状态说明 + 两句提示"收成**一个组件**，
 * 三条队列与今日打卡页都渲染它，差别只用 props 表达：
 *
 *  - `tone`：配色（当天 `brand` / 还有效 `sky` / 超时 `amber`）—— **只影响颜色**；
 *  - `kid`：儿童模式的字号（`text-4xl` vs `text-2xl`）；
 *  - `live`：这一端**是不是正在计时的那一端**。
 *
 * ⛔ 不要再复制这块 JSX。判据也⛔ 不要再写一遍 —— 全部在
 * `@fht/shared` 的 `describeTimerPanel()` 里（纯函数，有单测）。
 *
 * ## `live` 是干什么的（家长端必须传 false）
 *
 * 只有 `live` 时这个组件才会：
 *   - 挂 `useServerTimer` 的 1 秒 tick（让数字跳起来）；
 *   - 每 30 秒发一次心跳（`/checkins/:id/heartbeat`）；
 *   - 到预计时长时响一声（`useEstimateChime`）。
 *
 * 家长端**只读**（心跳端点对家长返回 403，见 `checkins.service.ts` 的
 * `heartbeatCheckin` 里那个 `canCheckin`），所以那边只显示**服务端快照**：
 * 数字照样有（这正是"看进度"需要的东西），但不会去推进、不会响、也不会
 * 把一次 403 显示成"网络不太好"。默认就是 `false`（安全的那一侧）。
 */
import { describeTimerPanel } from '@fht/shared';
import type { CheckinStatus } from '@fht/shared';
import { useServerTimer } from '../../lib/useServerTimer';
import { useEstimateChime } from '../../lib/useEstimateChime';
import { cn } from '../../lib/utils';

export type TimerPanelTone = 'brand' | 'sky' | 'amber';

/**
 * 配色 → 数字的颜色类。
 *
 * ⚠️ 写成**字面量**的 `Record`（不是拼字符串）：Tailwind 的 JIT 是扫源码的，
 * `text-${tone}-700` 这种拼出来的类名扫不到，三档颜色里会有两档悄悄失效。
 */
const TONE_TEXT: Record<TimerPanelTone, string> = {
  brand: 'text-brand-700',
  sky: 'text-sky-700',
  amber: 'text-amber-800',
};

export interface CheckinTimerPanelProps {
  /** **checkinId**（计时/心跳都用它，不是 habitId）。 */
  checkinId: string;
  /** 这一行的状态（与 `checkin.status` 同源）。 */
  status: CheckinStatus;
  /**
   * 服务端确认过的已用秒数。
   *
   * ⚠️ 队列行（超时 / 还有效）读的是服务端用 `recompute_checkin_seconds()`
   * **实时算过**的值，不是 `checkin.actual_seconds` 那一列的旧快照 ——
   * 那一列在 pause/resume/heartbeat 时都不写回（见 shared 的
   * `LIVE_TIMER_STATUSES`）。前端拿不到实时值时宁可少显示，⛔ 不要自己乘。
   */
  actualSeconds: number;
  /** 「预计 X」用的秒数（决定要不要提示"超过两倍"）。 */
  estimatedSeconds?: number | null;
  /** 儿童模式：更大的字号与间距。 */
  kid?: boolean;
  /** 配色（只影响数字颜色，见 `TONE_TEXT`）。 */
  tone?: TimerPanelTone;
  /** 见文件头：只有**正在计时的那一端**才传 `true`。家长端保持默认 `false`。 */
  live?: boolean;
  className?: string;
}

export function CheckinTimerPanel({
  checkinId,
  status,
  actualSeconds,
  estimatedSeconds = null,
  kid = false,
  tone = 'brand',
  live = false,
  className,
}: CheckinTimerPanelProps) {
  const isRunning = status === 'running';
  /**
   * 只有"这一端在计时"且"真的在跑"时才把 checkin 交给 `useServerTimer`：
   * 传 `null` = 不挂 tick、不发心跳（家长端 / 暂停中 / 终态）。
   */
  const driving = live && isRunning && checkinId !== '';

  /**
   * ⚠️ 只喂 hook 真正读的三个字段（`id` / `status` / `actualSeconds`）：
   * `useServerTimer` 的入参是 `ServerTimerSource`（比 `Checkin` 更窄）——
   * 所以这里**不需要伪造** `startedAt` / `valueDone` 之类的业务字段，
   * 而"绝不解析 startedAt 再与本地时间相减"这条约束因此是结构性的。
   */
  const timer = useServerTimer(
    driving ? { id: checkinId, status, actualSeconds } : null,
  );

  const view = describeTimerPanel({
    status,
    serverSeconds: actualSeconds,
    tickingSeconds: driving ? timer.elapsedSeconds : null,
    estimatedSeconds,
    heartbeatFailed: driving && timer.heartbeatFailed,
  });

  /**
   * 到预计时长响一声（判据在 shared 的 `advanceEstimateChime`，逐条测过：
   * 只响一次、暂停不响、没有预计时长不响）。喂的就是面板上那两个数。
   */
  useEstimateChime({
    sessionId: driving ? checkinId : null,
    isRunning: driving,
    elapsedSeconds: view.elapsedSeconds,
    serverSeconds: actualSeconds,
    estimatedSeconds,
  });

  if (!view.visible) return null;

  return (
    <div
      className={cn(
        'mt-2 flex flex-wrap items-center gap-3 rounded-xl bg-slate-50 px-3 py-2',
        kid && 'px-4 py-3',
        className,
      )}
    >
      <p
        /* `aria-live="off"`：每秒变一次的数字不要被读屏逐秒播报 */
        className={cn('font-bold tabular-nums', TONE_TEXT[tone], kid ? 'text-4xl' : 'text-2xl')}
        aria-live="off"
        /* 测试/排障锚点：这一行到底有没有"已用时间"（见 web 的计时契约用例） */
        data-timer-clock={view.clock}
      >
        {view.clock}
      </p>
      <span className={cn('text-slate-500', kid ? 'text-base' : 'text-xs')}>{view.note}</span>
      {view.hints.network && (
        <span className="text-xs text-amber-600">{view.hints.network}</span>
      )}
      {view.hints.autoPause && (
        <span className="text-xs text-amber-700">{view.hints.autoPause}</span>
      )}
    </div>
  );
}
